import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import api from 'api';
import { PortfoilioLead } from 'api/portfolio/buyer';
import ResolvePagination from 'components/ResolvePagination';
import useQueryParams from 'hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import WatchlistPortfolioCard from '../../../Watchlist/components/WatchlistPortfolioCard';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from './components/ProspectiveBuyersTableCells/data';
import {
  CUSTOM_COL_SIZES,
  REQUIRED_COLUMN,
  PRIVATE_INVITATION_LIMIT,
  PRIVATE_INVITATION_PAGE,
} from '../TableCells/data';
import { BODY_CONTENT_GENERATE } from './components/ProspectiveBuyersTableCells';
import utils, {
  safeStringify,
  getIsArrayEmpty,
  getFilterOptionByValue,
} from 'utils';
import { getApiRangesFromParams } from 'components/RangeFilterWrapper/utils';
import { useNavigate } from 'react-router-dom';
import PortfolioPlaceholderCards from '../../../../components/PortfolioPlaceholderCards';
import { SIZE } from 'models/Size';
import usePageParams from 'hooks/usePageParams';
import useScrollIntoView from 'hooks/useScrollIntoView';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import ManageColumns from 'components/ManageColumns';
import useMultiSort from 'hooks/useMultiSort';
import MultiSortPanel from 'components/MultiSortPanel';
import MultiFilterPanel from 'components/MultiFilterPanel';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import useProspectiveBuyerFilterOptions from './useProspectiveBuyerFilterOptions';
import { ASCENDING_DESCENDING_COPY, EARLIEST_LATEST_COPY } from 'models/Sort';
import { PF_GRID_VIEW_LIMIT, PRIVATE_DEFAULT_SORT } from '../../../../../data';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useTableFetchState from 'hooks/useTableFetchState';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';

import './ProspectiveBuyer.styles.scss';

const MULTI_SORT_DIRECTION_COPY = {
  [SORTING_VALUES.price]: ASCENDING_DESCENDING_COPY,
  [SORTING_VALUES.ebitda]: ASCENDING_DESCENDING_COPY,
  [SORTING_VALUES.createdAt]: EARLIEST_LATEST_COPY,
};

type ProspectiveBuyerProps = {
  className?: string;
  isTableView?: boolean;
  isEmpty: boolean;
  setIsEmptyPrivateInvitations: (value: boolean) => void;
};

const ProspectiveBuyer: React.FC<ProspectiveBuyerProps> = (props) => {
  const { className, isTableView, isEmpty, setIsEmptyPrivateInvitations } =
    props;

  const classes = classNames('prospective-buyer', className);

  const {
    isLoading,
    totalPages,
    totalItems,
    data: deals,
    setIsLoading,
    setPaginatedData,
  } = useTableFetchState<PortfoilioLead>();

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onColumnSizingChange,
    onReorder,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.PROSPECTIVE_BUYER,
    TABLE_COLUMNS,
  );

  const {
    params: {
      privateInvitationSort,
      listingTimeframe,
      listingInvited,
      listingFrom,
      listingTo,
      listingEbitdaFrom,
      listingEbitdaTo,
      listingLocation,
      listingIndustry,
    },
    setQueryParam,
  } = useQueryParams();

  const filterOptions = useProspectiveBuyerFilterOptions();

  const { limit, updateLimit } = useNumberOfResultsPerPage({
    limitParam: PRIVATE_INVITATION_LIMIT,
    pageParam: PRIVATE_INVITATION_PAGE,
  });

  const dataLimit = useMemo(
    () => `${isTableView ? limit : PF_GRID_VIEW_LIMIT}`,
    [isTableView, limit],
  );

  const [contentRef, scrollToStart] = useScrollIntoView();

  const privateInvitationPage = usePageParams(PRIVATE_INVITATION_PAGE);

  const { multiSortValues, multiSortDirectionTranslation } = useMultiSort(
    columns,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const columnFilterOptions = useMemo(
    () => ({
      price: {
        options: getFilterOptionByValue(filterOptions, 'listingTo'),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.price],
      },
      ebitda: {
        options: getFilterOptionByValue(filterOptions, 'listingEbitdaTo'),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.ebitda],
      },
      state: {
        options: getFilterOptionByValue(filterOptions, 'listingLocation'),
      },
      industry: {
        options: getFilterOptionByValue(filterOptions, 'listingIndustry'),
      },
      createdAt: {
        options: getFilterOptionByValue(filterOptions, 'listingInvited'),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.createdAt],
      },
    }),
    [filterOptions, multiSortDirectionTranslation],
  );

  const renderDeals = useMemo(
    () =>
      isLoading ? (
        <PortfolioPlaceholderCards
          numberOfCards={+dataLimit}
          size={SIZE.SMALL}
        />
      ) : (
        deals.map(
          ({
            dealRoom: {
              _id,
              stage,
              listingStage,
              createdAt,
              industry,
              dealOutcome,
              otherDealOutcome,
              portfolioName,
              isColleague,
              userId,
              user,
            },
            listing: {
              gallery,
              type,
              expirationDate,
              seekingExpressionOfInterestDate,
              slugName,
            },
          }) => (
            <WatchlistPortfolioCard
              key={_id}
              imageSrc={gallery[0]?.url}
              title={portfolioName}
              dealStage={stage}
              listingStage={listingStage}
              date={createdAt}
              isColleague={isColleague}
              dealOutcome={dealOutcome}
              otherDealOutcome={otherDealOutcome}
              listingType={type}
              industry={industry}
              seekingExpressionOfInterestDate={seekingExpressionOfInterestDate}
              expirationDate={expirationDate}
              ownerId={userId}
              owner={user}
              slugName={slugName}
            />
          ),
        )
      ),
    [dataLimit, deals, isLoading],
  );

  const tableContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: ({ dealRoom, listing, createdAt }: PortfoilioLead) =>
            BODY_CONTENT_GENERATE[col]({
              dealRoom,
              listing,
              createdAt,
            }),
        }),
        {},
      ),
    [],
  );

  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const priceRanges = getApiRangesFromParams(listingFrom, listingTo);
        const ebitdaRanges = getApiRangesFromParams(
          listingEbitdaFrom,
          listingEbitdaTo,
        );

        const { data } = await api.portfolio.buyer.getProspectiveBuyersListings(
          {
            $page: privateInvitationPage,
            $limit: dataLimit,
            $sort: privateInvitationSort || PRIVATE_DEFAULT_SORT,
            $relations: '["listing","buyer","listingEnquiry","dealRoom"]',
            timeframe: listingTimeframe,
            invited: listingInvited,
            priceRanges: safeStringify(priceRanges),
            ebitdaRanges: safeStringify(ebitdaRanges),
            location: listingLocation,
            industry: listingIndustry,
          },
        );

        setPaginatedData(data);
        setIsEmptyPrivateInvitations(getIsArrayEmpty(data.items));
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    privateInvitationPage,
    privateInvitationSort,
    listingTimeframe,
    listingInvited,
    listingFrom,
    listingTo,
    listingEbitdaFrom,
    listingEbitdaTo,
    listingLocation,
    listingIndustry,
    dataLimit,
    setIsLoading,
    setIsEmptyPrivateInvitations,
    setPaginatedData,
  ]);

  const hasActiveFilters = useMemo(
    () =>
      !!(
        listingLocation ||
        listingIndustry ||
        listingFrom ||
        listingTo ||
        listingEbitdaFrom ||
        listingEbitdaTo ||
        listingTimeframe ||
        listingInvited
      ),
    [
      listingLocation,
      listingIndustry,
      listingFrom,
      listingTo,
      listingEbitdaFrom,
      listingEbitdaTo,
      listingTimeframe,
      listingInvited,
    ],
  );

  if (isEmpty && !hasActiveFilters) return null;

  return (
    <div className={classes} ref={contentRef}>
      <div className="table-heading">
        <p className="title">{t('listingInvitations')}</p>

        <div className="browse-portfolio-heading__right">
          {isTableView && !isEmpty && (
            <NumberOfResultsPerPageFilter
              limit={limit}
              updateLimit={updateLimit}
            />
          )}
          <MultiFilterPanel ref={multiFilterRef} filters={filterOptions} />
          <MultiSortPanel
            ref={multiSortRef}
            options={multiSortValues}
            sortDirectionTranslations={multiSortDirectionTranslation}
            sortParam="privateInvitationSort"
          />
          {isTableView && (
            <ManageColumns
              allColumns={allColumns}
              hiddenColumns={hiddenColumns}
              headingTranslations={HEADING_TRANSLATIONS}
              lockedColumn={REQUIRED_COLUMN}
              isDefaultColumns={isDefaultColumns}
              onSave={saveColumnSettings}
              onResetToDefault={resetToDefault}
            />
          )}
        </div>
      </div>

      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={multiSortValues}
        sortParam="privateInvitationSort"
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      <div className="resolve-page-items">
        {!isTableView ? (
          <>
            {renderDeals}
            <ResolvePagination
              totalPages={totalPages}
              pageParam={PRIVATE_INVITATION_PAGE}
              onClick={scrollToStart}
            />
          </>
        ) : (
          <ReorderResizeTable
            className="portfolio-results-table"
            headingTranslations={HEADING_TRANSLATIONS}
            tableContentTransformations={tableContent}
            columns={columns as Array<keyof PortfoilioLead>}
            sortingValues={SORTING_VALUES}
            columnFilterOptions={columnFilterOptions}
            isDataLoading={isLoading}
            data={deals}
            customSizes={CUSTOM_COL_SIZES}
            sort={privateInvitationSort}
            totalPages={totalPages}
            totalItems={totalItems}
            recordName="invitation"
            pageParam={PRIVATE_INVITATION_PAGE}
            updateSort={(value) =>
              setQueryParam('privateInvitationSort', value)
            }
            onRowClick={({ listing: { industry, slugName, type } }) =>
              navigate(utils.composeBusinessUrl(industry, slugName, type))
            }
            onReorder={
              onReorder as (columns: Array<keyof PortfoilioLead>) => void
            }
            columnSizing={columnSizing}
            onColumnSizingChange={onColumnSizingChange}
            onPaginationClick={scrollToStart}
          />
        )}
      </div>
    </div>
  );
};

export default ProspectiveBuyer;
